import React, { useState, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    Box,
    Grid,
    Tabs,
    Tab,
    CircularProgress,
} from '@material-ui/core'
import {
    ArrowBack as ArrowBackIcon,
    Mail as MailIcon,
    Visibility as EyeIcon,
    TouchApp as MousePointerIcon,
    Pause as PauseIcon,
    PlayArrow as PlayIcon,
    Flag as TargetIcon,
    People as PeopleIcon,
} from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'

import { Routes } from '../../../../navigation/config/routes'
import { TabPanel } from '../../../../components/TabPanel/TabPanel'
import { useCampaignDetail } from '../../../../hooks/Outreach/useCampaign'
import {
    CampaignStepType,
    CampaignStatus,
    Campaign,
    CampaignStep,
    CampaignProspect,
    CampaignProspectActivity,
} from '../../../../models/Outreach/types'
import { EditCampaignModal } from './EditCampaignModal'
import { MessageSequenceTab } from './MessageSequenceTab'
import { RecentActivityTab } from './RecentActivityTab'
import { ProspectTrackingTab } from './ProspectTrackingTab'

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
    },
    heroSection: {
        background: `linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #06b6d4 100%)`,
        borderRadius: theme.spacing(4),
        padding: theme.spacing(4),
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    },
    heroTitle: {
        fontSize: '2.5rem',
        fontWeight: 'bold',
        marginBottom: theme.spacing(1),
    },
    heroSubtitle: {
        fontSize: '1rem',
        opacity: 0.9,
    },
    backButton: {
        marginBottom: theme.spacing(2),
        backgroundColor: '#60a5fa',
        color: 'white',
        '&:hover': {
            backgroundColor: '#3b82f6',
        },
        borderRadius: '6px',
        textTransform: 'none',
        fontWeight: 500,
    },
    actionButton: {
        backgroundColor: 'transparent',
        color: 'white',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        borderRadius: theme.spacing(1),
        textTransform: 'none',
        fontWeight: 500,
        padding: theme.spacing(1.5, 3),
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.5)',
        },
        '& .MuiButton-startIcon': {
            marginRight: theme.spacing(1),
        },
    },
    metricCard: {
        border: 'none',
        backgroundColor: '#ffffff',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        transition: 'all 0.3s ease',
        height: '100%',
        '&:hover': {
            boxShadow:
                '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        },
    },
    metricCardContent: {
        padding: theme.spacing(3),
    },
    metricIcon: {
        padding: theme.spacing(1),
        borderRadius: theme.spacing(1.5),
        marginRight: theme.spacing(1.5),
    },
    metricValue: {
        fontSize: '1.875rem',
        fontWeight: 'bold',
        lineHeight: 1.2,
    },
    metricLabel: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.5),
    },
    tabsWrapper: {
        marginLeft: `calc(-${theme.spacing(3)} - 24px)`,
        marginRight: `calc(-${theme.spacing(3)} - 24px)`,
        marginBottom: theme.spacing(3),
        width: `calc(100% + ${theme.spacing(3)} + ${theme.spacing(3)} + 48px)`,
    },
    tabsPadding: {
        paddingLeft: `calc(${theme.spacing(3)} + 24px)`,
        paddingRight: `calc(${theme.spacing(3)} + 24px)`,
    },
    tabContent: {
        width: '100%',
        paddingLeft: `calc(${theme.spacing(3)} + 24px)`,
        paddingRight: `calc(${theme.spacing(3)} + 24px)`,
    },
    tabsContainer: {
        backgroundColor: '#ffffff',
        border: '1px solid rgba(226, 232, 240, 0.5)',
        borderRadius: theme.spacing(1),
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginBottom: theme.spacing(3),
        padding: theme.spacing(0.5),
    },
    tab: {
        textTransform: 'none',
        fontSize: '0.875rem',
        fontWeight: 500,
        minWidth: 120,
        borderRadius: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
        padding: theme.spacing(1, 2),
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(59, 130, 246, 0.05)',
        },
        '&.Mui-selected': {
            backgroundColor: '#3b82f6',
            color: 'white',
            fontWeight: 600,
        },
    },
    sequenceCard: {
        border: 'none',
        backgroundColor: '#ffffff',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'hidden',
        marginBottom: theme.spacing(2),
        '&:hover': {
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        },
    },
    stepIndicator: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 4,
    },
    stepIcon: {
        padding: theme.spacing(1.5),
        borderRadius: theme.spacing(2),
        transition: 'transform 0.3s ease',
    },
    metricsContainer: {
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1.5),
        background: `linear-gradient(135deg, #f8fafc, #f1f5f9)`,
        marginTop: theme.spacing(2),
    },
    metricsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: theme.spacing(2),
        textAlign: 'center',
    },
    waitBadge: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(1.5),
    },
    activityCard: {
        border: 'none',
        backgroundColor: '#ffffff',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    activityItem: {
        display: 'flex',
        alignItems: 'flex-start',
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1.5),
        marginBottom: theme.spacing(1),
        transition: 'background-color 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(248, 250, 252, 0.5)',
        },
    },
    activityIcon: {
        padding: theme.spacing(1),
        borderRadius: theme.spacing(1.5),
        marginRight: theme.spacing(2),
    },
    tableContainer: {
        border: 'none',
        backgroundColor: '#ffffff',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    table: {
        minWidth: 650,
    },
    tableHead: {
        backgroundColor: '#f8fafc',
        '& .MuiTableCell-head': {
            fontWeight: 600,
            color: '#1f2937',
            fontSize: '0.875rem',
        },
    },
    tableRow: {
        '&:hover': {
            backgroundColor: 'rgba(248, 250, 252, 0.5)',
        },
    },
    statusChip: {
        fontSize: '0.75rem',
        height: 24,
    },
}))

export const CampaignDetail = () => {
    const classes = useStyles()
    const { id } = useParams<{ id: string }>()
    const [selectedTab, setSelectedTab] = useState(0)
    const [editModalOpen, setEditModalOpen] = useState(false)
    const {
        campaignDetail,
        isLoadingCampaignDetail,
        errorCampaignDetail,
        updateCampaignStatus,
        updateCampaign,
        campaignProspects,
        isLoadingCampaignProspects,
        errorCampaignProspects,
        campaignProspectActivities,
        isLoadingCampaignProspectActivities,
        errorCampaignProspectActivities,
    } = useCampaignDetail(id)

    // Get prospects data from the response
    const prospects: CampaignProspect[] = (campaignProspects as any)?.data || []

    // Get activities data from the response
    const activities: CampaignProspectActivity[] =
        (campaignProspectActivities as any)?.data || []

    // Transform backend campaign data to UI format
    const campaign = useMemo(() => {
        if (!campaignDetail) {
            return null
        }

        // Determine campaign type based on steps
        const hasEmail = campaignDetail.steps.some(
            (step) => step.step_type === CampaignStepType.EMAIL,
        )
        const hasSms = campaignDetail.steps.some(
            (step) => step.step_type === CampaignStepType.TEXT_MESSAGE,
        )
        let type = 'Multi-step Campaign'
        if (hasEmail && hasSms) {
            type = 'Multi-step Email & SMS Sequence'
        } else if (hasEmail) {
            type = 'Multi-step Email Sequence'
        } else if (hasSms) {
            type = 'Multi-step SMS Sequence'
        }

        // Format created date
        const createdAt = campaignDetail.created_date
            ? new Date(campaignDetail.created_date).toLocaleDateString()
            : 'Unknown'

        // Map status from CampaignStatus enum or active boolean
        let status = 'paused'
        if (campaignDetail.status) {
            status = campaignDetail.status.toLowerCase()
        } else if (campaignDetail.active) {
            status = 'active'
        }

        return {
            id: campaignDetail.id,
            name: campaignDetail.name,
            type: type,
            status: status,
            totalSent: campaignDetail.total_sent || 0,
            opens: campaignDetail.total_opens || 0,
            clicks: campaignDetail.total_clicks || 0,
            conversions: campaignDetail.total_converted || 0,
            totalProspects: campaignDetail.total_prospects || 0,
            createdAt: createdAt,
        }
    }, [campaignDetail])

    // Transform backend steps to UI sequence format
    const sequence = useMemo(() => {
        if (!campaignDetail || !campaignDetail.steps) {
            return []
        }

        // Filter out delay steps and transform message steps
        const messageSteps = campaignDetail.steps
            .filter(
                (step) =>
                    step.step_type === CampaignStepType.EMAIL ||
                    step.step_type === CampaignStepType.TEXT_MESSAGE,
            )
            .map((step, index) => {
                // Find the delay before this step (if any)
                // Look for the step immediately before this one in step_order
                const currentStepOrder = step.step_order
                const previousStep = campaignDetail.steps.find(
                    (s) =>
                        s.step_order === currentStepOrder - 1 &&
                        s.step_type === CampaignStepType.DELAY,
                )
                const delay = previousStep?.delay_days || 0

                return {
                    step: step.step_order,
                    type: (step.step_type === CampaignStepType.EMAIL
                        ? 'email'
                        : 'sms') as 'email' | 'sms',
                    subject:
                        step.subject || step.content?.substring(0, 50) || '',
                    delay: delay,
                    sent: step.total_sent || 0,
                    opened: step.total_opens || 0,
                    clicked: step.total_clicks || 0,
                    converted: step.total_converted || 0,
                }
            })

        return messageSteps
    }, [campaignDetail])

    // Transform activities to UI format
    const recentActivity = useMemo(() => {
        return activities.map((activity) => {
            // Map backend activity types to frontend types
            let activityType: 'conversion' | 'click' | 'open' | 'sent' = 'sent'

            if (activity.type === 'email_clicked') {
                activityType = 'click'
            } else if (activity.type === 'email_opened') {
                activityType = 'open'
            } else if (
                activity.type === 'email_sent' ||
                activity.type === 'text_sent'
            ) {
                activityType = 'sent'
            }

            return {
                type: activityType,
                prospect: activity.identifier,
                action: activity.action,
                time: activity.relative_time,
            }
        })
    }, [activities])

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active':
                return 'primary'
            case 'completed':
                return 'default'
            case 'paused':
                return 'secondary'
            default:
                return 'default'
        }
    }

    const calculateRate = (numerator: number, denominator: number) => {
        if (denominator === 0) return '0%'
        return `${Math.round((numerator / denominator) * 100)}%`
    }

    const handlePauseCampaign = () => {
        if (campaignDetail) {
            updateCampaignStatus.mutate({
                id: campaignDetail.id,
                status: CampaignStatus.PAUSED,
            })
        }
    }

    const handleResumeCampaign = () => {
        if (campaignDetail) {
            updateCampaignStatus.mutate({
                id: campaignDetail.id,
                status: CampaignStatus.ACTIVE,
            })
        }
    }

    const handleExportReport = () => {
        toast.info('Report export started')
    }

    const handleEditCampaign = (
        updates: Partial<Campaign>,
        steps: CampaignStep[],
    ) => {
        if (!id || !campaignDetail) return

        updateCampaign.mutate(
            {
                id: parseInt(id),
                name: updates.name,
                campaignGoal: updates.campaign_goal,
                steps: steps,
                replyToEmail: updates.reply_to_email,
                senderName: updates.sender_name,
                concessions: updates.concessions,
            },
            {
                onSuccess: () => {
                    setEditModalOpen(false)
                },
            },
        )
    }

    // Loading state
    if (isLoadingCampaignDetail) {
        return (
            <Box
                className={classes.container}
                display="flex"
                justifyContent="center"
                alignItems="center"
                style={{ minHeight: '400px' }}
            >
                <CircularProgress />
            </Box>
        )
    }

    // Error state
    if (errorCampaignDetail || !campaign) {
        return (
            <Box className={classes.container}>
                <Typography variant="h6" color="error">
                    Failed to load campaign details
                </Typography>
                <Link
                    to={Routes.manage.campaign.root}
                    style={{ textDecoration: 'none' }}
                >
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        style={{ marginTop: 16 }}
                    >
                        Back to Campaigns
                    </Button>
                </Link>
            </Box>
        )
    }

    return (
        <Box className={classes.container}>
            {/* Hero Header */}
            <Box className={classes.heroSection}>
                <Box>
                    <Link
                        to={Routes.manage.campaign.root}
                        style={{ textDecoration: 'none' }}
                    >
                        <Button
                            className={classes.backButton}
                            startIcon={<ArrowBackIcon />}
                            size="small"
                        >
                            Back to Campaigns
                        </Button>
                    </Link>

                    <Grid container spacing={3} alignItems="flex-start">
                        <Grid item xs={12} md={8}>
                            <Box flex={1}>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    style={{ gap: 12, marginBottom: 12 }}
                                >
                                    <Typography
                                        variant="h3"
                                        className={classes.heroTitle}
                                    >
                                        {campaign.name}
                                    </Typography>
                                    <Chip
                                        label={
                                            campaign.status
                                                .charAt(0)
                                                .toUpperCase() +
                                            campaign.status.slice(1)
                                        }
                                        color={
                                            getStatusColor(campaign.status) as
                                                | 'primary'
                                                | 'default'
                                                | 'secondary'
                                        }
                                        size="small"
                                    />
                                </Box>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    style={{ gap: 12 }}
                                    className={classes.heroSubtitle}
                                >
                                    <Typography>{campaign.type}</Typography>
                                    <Typography>•</Typography>
                                    <Typography>
                                        Created {campaign.createdAt}
                                    </Typography>
                                    <Typography>•</Typography>
                                    <Typography>
                                        {sequence.length} messages
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            md={4}
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Box display="flex" style={{ gap: 12 }}>
                                {campaign.status === 'active' ||
                                campaignDetail?.status ===
                                    CampaignStatus.ACTIVE ? (
                                    <Button
                                        className={classes.actionButton}
                                        variant="outlined"
                                        size="large"
                                        startIcon={<PauseIcon />}
                                        onClick={handlePauseCampaign}
                                    >
                                        Pause Campaign
                                    </Button>
                                ) : (
                                    <Button
                                        className={classes.actionButton}
                                        variant="outlined"
                                        size="large"
                                        startIcon={<PlayIcon />}
                                        onClick={handleResumeCampaign}
                                    >
                                        Resume Campaign
                                    </Button>
                                )}
                                <Button
                                    className={classes.actionButton}
                                    variant="outlined"
                                    size="large"
                                    onClick={() => setEditModalOpen(true)}
                                >
                                    Edit Campaign
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Box>

            {/* Key Metrics */}
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={2}>
                    <Card className={classes.metricCard}>
                        <CardContent className={classes.metricCardContent}>
                            <Box
                                display="flex"
                                alignItems="center"
                                style={{ gap: 12 }}
                            >
                                <Box
                                    className={classes.metricIcon}
                                    style={{
                                        backgroundColor:
                                            'rgba(99, 102, 241, 0.1)',
                                    }}
                                >
                                    <PeopleIcon
                                        style={{
                                            color: '#6366f1',
                                            fontSize: 20,
                                        }}
                                    />
                                </Box>
                                <Box>
                                    <Typography className={classes.metricLabel}>
                                        Total Prospects
                                    </Typography>
                                    <Typography
                                        className={classes.metricValue}
                                        style={{ color: '#6366f1' }}
                                    >
                                        {campaign.totalProspects.toLocaleString()}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <Card className={classes.metricCard}>
                        <CardContent className={classes.metricCardContent}>
                            <Box
                                display="flex"
                                alignItems="center"
                                style={{ gap: 12 }}
                            >
                                <Box
                                    className={classes.metricIcon}
                                    style={{
                                        backgroundColor:
                                            'rgba(59, 130, 246, 0.1)',
                                    }}
                                >
                                    <MailIcon
                                        style={{
                                            color: '#3b82f6',
                                            fontSize: 20,
                                        }}
                                    />
                                </Box>
                                <Box>
                                    <Typography className={classes.metricLabel}>
                                        Total Sent
                                    </Typography>
                                    <Typography
                                        className={classes.metricValue}
                                        style={{ color: '#3b82f6' }}
                                    >
                                        {campaign.totalSent.toLocaleString()}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <Card className={classes.metricCard}>
                        <CardContent className={classes.metricCardContent}>
                            <Box
                                display="flex"
                                alignItems="center"
                                style={{ gap: 12 }}
                            >
                                <Box
                                    className={classes.metricIcon}
                                    style={{
                                        backgroundColor:
                                            'rgba(139, 92, 246, 0.1)',
                                    }}
                                >
                                    <EyeIcon
                                        style={{
                                            color: '#8b5cf6',
                                            fontSize: 20,
                                        }}
                                    />
                                </Box>
                                <Box>
                                    <Typography className={classes.metricLabel}>
                                        Open Rate
                                    </Typography>
                                    <Typography
                                        className={classes.metricValue}
                                        style={{ color: '#8b5cf6' }}
                                    >
                                        {calculateRate(
                                            campaign.opens,
                                            campaign.totalSent,
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <Card className={classes.metricCard}>
                        <CardContent className={classes.metricCardContent}>
                            <Box
                                display="flex"
                                alignItems="center"
                                style={{ gap: 12 }}
                            >
                                <Box
                                    className={classes.metricIcon}
                                    style={{
                                        backgroundColor:
                                            'rgba(16, 185, 129, 0.1)',
                                    }}
                                >
                                    <MousePointerIcon
                                        style={{
                                            color: '#10b981',
                                            fontSize: 20,
                                        }}
                                    />
                                </Box>
                                <Box>
                                    <Typography className={classes.metricLabel}>
                                        Click Rate
                                    </Typography>
                                    <Typography
                                        className={classes.metricValue}
                                        style={{ color: '#10b981' }}
                                    >
                                        {calculateRate(
                                            campaign.clicks,
                                            campaign.totalSent,
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <Card className={classes.metricCard}>
                        <CardContent className={classes.metricCardContent}>
                            <Box
                                display="flex"
                                alignItems="center"
                                style={{ gap: 12 }}
                            >
                                <Box
                                    className={classes.metricIcon}
                                    style={{
                                        backgroundColor:
                                            'rgba(245, 158, 11, 0.1)',
                                    }}
                                >
                                    <TargetIcon
                                        style={{
                                            color: '#f59e0b',
                                            fontSize: 20,
                                        }}
                                    />
                                </Box>
                                <Box>
                                    <Typography className={classes.metricLabel}>
                                        Conversions
                                    </Typography>
                                    <Typography
                                        className={classes.metricValue}
                                        style={{ color: '#f59e0b' }}
                                    >
                                        {campaign.conversions}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Main Content Tabs */}
            <Box className={classes.tabsWrapper}>
                <Box className={classes.tabsPadding}>
                    <Tabs
                        value={selectedTab}
                        onChange={(_, newValue) => setSelectedTab(newValue)}
                        className={classes.tabsContainer}
                        TabIndicatorProps={{ style: { display: 'none' } }}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        <Tab label="Message Sequence" className={classes.tab} />
                        <Tab label="Recent Activity" className={classes.tab} />
                        <Tab
                            label="Prospect Tracking"
                            className={classes.tab}
                        />
                    </Tabs>
                </Box>

                {/* Sequence View */}
                <TabPanel value={selectedTab} index={0}>
                    <MessageSequenceTab
                        sequence={sequence}
                        campaignDetail={campaignDetail || null}
                        calculateRate={calculateRate}
                    />
                </TabPanel>

                {/* Activity Feed */}
                <TabPanel value={selectedTab} index={1}>
                    <RecentActivityTab
                        recentActivity={recentActivity}
                        isLoading={isLoadingCampaignProspectActivities}
                        error={errorCampaignProspectActivities}
                    />
                </TabPanel>

                {/* Prospect Tracking */}
                <TabPanel value={selectedTab} index={2}>
                    <ProspectTrackingTab
                        prospects={prospects}
                        isLoading={isLoadingCampaignProspects}
                        error={errorCampaignProspects as Error | null}
                    />
                </TabPanel>
            </Box>

            {/* Edit Campaign Modal */}
            <EditCampaignModal
                open={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                campaign={campaignDetail || null}
                onSave={handleEditCampaign}
                isSaving={updateCampaign.isLoading}
            />
        </Box>
    )
}
